// Landing page with integrated login and auth management
import "./styles/landing.css";
import { LocalStore } from "../LocalStore";

// Determine base URL based on environment
const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';


// Auth state management
interface AuthResponse {
  authenticated: boolean;
  user?: {
    id: number;
    name: string;
  };
  error?: string;
}

interface LoginFormData {
  username: string;
  password: string;
  rememberMe: boolean;
}

class AuthManager {
  private static instance: AuthManager;

  static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  async checkAuthStatus(): Promise<AuthResponse> {
    try {
      const response = await fetch('/api/auth-status.php', {
        method: 'GET',
        credentials: 'same-origin'
      });
      return await response.json();
    } catch (error) {
      console.error('Auth check failed:', error);
      return { authenticated: false };
    }
  }

  async login(username: string, password: string): Promise<AuthResponse> {
    try {
      const response = await fetch('/api/login.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ username, password })
      });

      const result = await response.json();
      const hasErrors = result.error || (Array.isArray(result.errors) && result.errors.length > 0);

      if (response.ok && !hasErrors) {
        // Signing in leaves the "no account" mode
        LocalStore.setGuestSession(false);
        // Redirect to app
        window.location.href = '/app/home';
        return { authenticated: true, user: { id: result.uid, name: result.username } };
      }

      const errorMessage = result.error || (Array.isArray(result.errors) ? result.errors[0] : undefined) || 'Invalid credentials';
      return { authenticated: false, error: errorMessage };
    } catch (error) {
      console.error('Login failed:', error);
      return { authenticated: false, error: 'Network error' };
    }
  }

  async logout(): Promise<void> {
    try {
      await fetch('/api/logout.php', {
        method: 'GET',
        credentials: 'same-origin'
      });
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  // Elements
  const loginFormContainer = document.getElementById('login-form-container');
  const loginForm = document.getElementById('login-form') as HTMLFormElement;
  const usernameInput = document.getElementById('username') as HTMLInputElement;
  const passwordInput = document.getElementById('password') as HTMLInputElement;
  const rememberMeCheckbox = document.getElementById('remember-me') as HTMLInputElement;
  const passwordToggleBtn = document.getElementById('password-toggle') as HTMLButtonElement;
  const socialLoginContainer = document.getElementById('social-login-container');

  let rememberCredentials = false;
  let retryCount = 0;
  const MAX_RETRIES = 3;

  // Check auth status on load
  checkInitialAuthState();

  async function checkInitialAuthState(): Promise<void> {
    const authManager = AuthManager.getInstance();
    const authStatus = await authManager.checkAuthStatus();

    if (authStatus.authenticated) {
      // The app would otherwise open in "no account" mode
      LocalStore.setGuestSession(false);
      showAuthenticatedState(authStatus.user!);
    }
  }

  document.getElementById('continue-guest')?.addEventListener('click', () => {
    LocalStore.setGuestSession(true);
    window.location.href = '/app/';
  });

  function showAuthenticatedState(user: { id: number; name: string }): void {
    if (loginFormContainer) {
      loginFormContainer.innerHTML = `
        <div class="authenticated-state">
          <div class="welcome-message">
            <h3>Welcome back, ${user.name}!</h3>
            <p>You are already signed in.</p>
          </div>
          <div class="auth-actions">
            <button id="go-to-app" class="btn btn-primary">Vai a Wallet</button>
            <button id="logout-btn" class="btn btn-secondary">Sign Out</button>
          </div>
        </div>
      `;

      loginFormContainer.classList.add('visible');

      // Bind new events
      document.getElementById('go-to-app')?.addEventListener('click', () => {
        window.location.href = '/app/';
      });

      document.getElementById('logout-btn')?.addEventListener('click', async () => {
        const authManager = AuthManager.getInstance();
        await authManager.logout();
      });
    }
  }

  async function handleLogin(e: Event): Promise<void> {
    e.preventDefault();

    const username = usernameInput?.value.trim();
    const password = passwordInput?.value;
    const rememberMe = rememberMeCheckbox?.checked;

    if (!username || !password) {
      showError('Please enter both username and password');
      return;
    }

    // Validate username format
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      showError('Username can only contain letters, numbers, and underscores');
      return;
    }

    setLoading(true);
    showLoadingSpinner(true);

    const authManager = AuthManager.getInstance();
    const result = await authManager.login(username, password);

    setLoading(false);
    showLoadingSpinner(false);

    if (!result.authenticated) {
      retryCount++;

      if (retryCount >= MAX_RETRIES) {
        showError('Too many failed attempts. Please try again later or contact support.');
        resetRetryCount();
      } else {
        const errorMessage = result.error || 'Invalid credentials';
        showError(errorMessage);

        // Shake animation for error
        loginForm?.classList.add('shake');
        setTimeout(() => {
          loginForm?.classList.remove('shake');
        }, 500);
      }
    } else {
      // Success case is handled in AuthManager (redirect)
      resetRetryCount();
    }
  }

  function setLoading(loading: boolean): void {
    const submitBtn = loginForm?.querySelector('button[type="submit"]') as HTMLButtonElement;
    if (submitBtn) {
      if (loading) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Signing in...';
      } else {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Sign In';
      }
    }
  }

  function showLoadingSpinner(show: boolean): void {
    const spinner = document.getElementById('loading-spinner');
    if (spinner) {
      spinner.style.display = show ? 'block' : 'none';
    }
  }

  function resetRetryCount(): void {
    retryCount = 0;
  }

  function showError(message: string): void {
    if (!loginForm) return;

    // Remove existing error
    const existingError = loginForm.querySelector('.error-message');
    if (existingError) {
      existingError.remove();
    }

    // Add new error
    const errorDiv = document.createElement('div');
    errorDiv.className = 'error-message';
    errorDiv.style.cssText = 'color: #ff6b6b; margin: 10px 0; font-size: 0.9rem; text-align: center; background: rgba(255, 107, 107, 0.1); padding: 10px; border-radius: 4px;';
    errorDiv.textContent = message;

    loginForm.insertBefore(errorDiv, loginForm.lastElementChild);

    // Remove error after 5 seconds
    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }

  // Event listeners
  loginForm?.addEventListener('submit', handleLogin);

  // Password visibility toggle
  passwordToggleBtn?.addEventListener('click', () => {
    const type = passwordInput?.getAttribute('type');
    if (passwordInput) passwordInput.setAttribute('type', type === 'password' ? 'text' : 'password');
    if (passwordToggleBtn) passwordToggleBtn.textContent = type === 'password' ? 'Hide Password' : 'Show Password';
  });

  // Remember me functionality
  rememberMeCheckbox?.addEventListener('change', (e) => {
    rememberCredentials = (e.target as HTMLInputElement).checked;
    if (rememberCredentials) {
      localStorage.setItem('remember_username', usernameInput?.value || '');
    } else {
      localStorage.removeItem('remember_username');
    }
  });

  // Auto-fill remembered username
  const rememberedUsername = localStorage.getItem('remember_username');
  if (rememberedUsername && rememberCredentials && usernameInput) {
    usernameInput.value = rememberedUsername;
  }

  // Social login placeholders
  if (socialLoginContainer) {
    socialLoginContainer.innerHTML = `
    <div class="social-login">
      <p>Or continue with:</p>
      <div class="social-buttons">
        <button type="button" class="social-btn google">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.76-.96 7.98-2.66V20.17c-1.31.77-2.89 1.23-4.59 1.23-3.39 0-6.28-2.49-6.62-5.79H5.4v2.65c0 3.27 2.36 6 5.4 6.63V23z"/>
            <path fill="#FBB92E" d="M12 5.16c.35-.34.79-.58 1.28-.72V2.44H7.5c-2.48 0-4.5 2.02-4.5 4.5s2.02 4.5 4.5 4.5h4.17c-.29 1.38-1.13 2.59-2.34 3.41v2.77c2.36-.7 4.38-2.4 5.46-4.66h3.12c-1.08 3.28-4.13 5.66-7.81 5.66-4.97 0-9-4.03-9-9s4.03-9 9-9z"/>
            <path fill="#EA4335" d="M12 16.25c1.26 0 2.42-.45 3.36-1.21l2.31 2.31c-.98.98-2.25 1.59-3.67 1.59-3.39 0-6.28-2.49-6.62-5.79H5.4v2.65c0 3.27 2.36 6 5.4 6.63V16.25z"/>
          </svg>
          Google
        </button>
        <button type="button" class="social-btn facebook">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.382H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.382C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          Facebook
        </button>
      </div>
    </div>
  `;

    // Animazioni per i bottoni
    const buttons = document.querySelectorAll('.btn');
    buttons.forEach(button => {
      button.addEventListener('mouseenter', () => {
        (button as HTMLElement).style.transform = 'translateY(-2px)';
      });
      button.addEventListener('mouseleave', () => {
        (button as HTMLElement).style.transform = 'translateY(0)';
      });
    });

    // Input validation with real-time feedback
    usernameInput?.addEventListener('blur', () => {
      validateInput(usernameInput, /^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores');
    });

    passwordInput?.addEventListener('blur', () => {
      validateInput(passwordInput, /.{6,}$/, 'Password must be at least 6 characters');
    });

    function validateInput(input: HTMLInputElement, pattern: RegExp, message: string): void {
      if (!input) return;

      if (!pattern.test(input.value)) {
        input.classList.add('invalid');
        input.setAttribute('aria-invalid', 'true');
      } else {
        input.classList.remove('invalid');
        input.setAttribute('aria-invalid', 'false');
      }
    }

    // Add focus styles for accessibility
    usernameInput?.addEventListener('focus', () => {
      usernameInput?.classList.add('focused');
    });

    usernameInput?.addEventListener('blur', () => {
      usernameInput?.classList.remove('focused');
    });

    passwordInput?.addEventListener('focus', () => {
      passwordInput?.classList.add('focused');
    });

    passwordInput?.addEventListener('blur', () => {
      passwordInput?.classList.remove('focused');
    });

    // Add keyboard navigation support
    loginForm?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        (loginForm?.querySelector('button[type="submit"]') as HTMLElement)?.click();
      }
    });
  }
});